import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Switch } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { CustomIconButton, DropDown, IconComponent, icon } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./ManageColumnsButton.module.css";
import { setPortfolioTableColumnOrder } from "../../slice/portfolio-space-slice";

export default function ManageColumnsButton() {
	const columnOrder = useSelector(({ portfolioSpace }) => portfolioSpace.tableColumnOrder);
	const dispatch = useDispatch();
	const anchorRef = useRef();
	const [open, setOpen] = useState(false);
	const [dragPortal] = useState(() => document.createElement("div"));
	const [lockedColumn, ...reorderableColumns] = columnOrder;

	useEffect(() => {
		document.body.appendChild(dragPortal);
		return () => document.body.removeChild(dragPortal);
	}, [dragPortal]);

	const handleDragEnd = (result) => {
		if (!result.destination || result.destination.index === result.source.index) {
			return;
		}
		const nextOrder = [...reorderableColumns];
		const [moved] = nextOrder.splice(result.source.index, 1);
		nextOrder.splice(result.destination.index, 0, moved);
		dispatch(setPortfolioTableColumnOrder([lockedColumn, ...nextOrder]));
	};
	const handleToggleVisibility = (key) => {
		dispatch(
			setPortfolioTableColumnOrder(
				columnOrder.map((colOrder) => {
					if (colOrder.key === key) {
						return { ...colOrder, visibility: !colOrder.visibility };
					}
					return colOrder;
				})
			)
		);
	};

	return (
		<>
			<CustomIconButton
				ref={anchorRef}
				icon={icon.faColumns}
				size="sm"
				tooltip={translate("dashboards.portfolio-space-manage-columns-button-tooltip")}
				variant="outlined"
				onClick={() => setOpen((prev) => !prev)}
			/>
			<DropDown ref={anchorRef} open={open} position="bottom-end" onClose={() => setOpen(false)}>
				<div className={styles.panel}>
					<p className={styles.helperText}>
						{translate("dashboards.portfolio-space-manage-columns-helper-text")}
					</p>
					{lockedColumn && (
						<div className={styles.row} data-locked="true">
							<IconComponent className={styles.gripIcon} icon={icon.faGripLines} />
							<span className={styles.label}>{lockedColumn.name}</span>
							<Switch checked disabled color="primary" size="small" />
						</div>
					)}
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId="manage-columns">
							{(dropProvided) => (
								<div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
									{reorderableColumns.map((col, index) => (
										<Draggable key={col.key} draggableId={col.key} index={index}>
											{(provided, snapshot) => {
												const row = (
													<div
														ref={provided.innerRef}
														className={styles.row}
														{...provided.draggableProps}
													>
														<span className={styles.gripIcon} {...provided.dragHandleProps}>
															<IconComponent icon={icon.faGripLines} />
														</span>
														<span className={styles.label}>{col.name}</span>
														<Switch
															checked={col.visibility !== false}
															color="primary"
															size="small"
															onChange={() => handleToggleVisibility(col.key)}
														/>
													</div>
												);
												return snapshot.isDragging ? createPortal(row, dragPortal) : row;
											}}
										</Draggable>
									))}
									{dropProvided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
			</DropDown>
		</>
	);
}
